import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import MaintenanceWindowEditDialog from '@totem/components/maintenanceWindows/maintenanceWindowEditDialog';
import MaintenanceWindowsV2Context, {
  DEFAULT_FILTERS,
} from '@totem/components/maintenanceWindowsV2/MaintenanceWindowsV2Context';
import {
  MaintenanceWindow,
  MaintenanceWindowFilters,
} from '@totem/components/maintenanceWindowsV2/types';
import useApprovalAction from '@totem/components/maintenanceWindowsV2/useApprovalAction';
import { matchesFilters } from '@totem/components/maintenanceWindowsV2/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

const MaintenanceWindowsV2Container = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [data, setData] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [now, setNow] = useState<number>(Date.now());
  const [filters, setFilterState] =
    useState<MaintenanceWindowFilters>(DEFAULT_FILTERS);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [editItem, setEditItem] = useState<MaintenanceWindow>(null);

  const refresh = () => setRefreshCount((count) => count + 1);
  const { sending, sendApproval } = useApprovalAction(refresh);

  // Org-wide list; the API scopes it to the current organization and the user's data access groups.
  useEffect(() => {
    setLoading(true);

    setFailed(false);
    const params: Params = {
      paging: { page: 1, pageSize: 10000 },
      sort: { field: 'details.scheduledStartTime', direction: -1 },
      filters: [],
    };
    fetch(`${DEVICES_ENDPOINT}/maintenanceWindows2`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify(params),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: MaintenanceWindow[]) => {
        setData(result ?? []);

        setNow(Date.now());
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [userProfile.organization.id, refreshCount, getApiToken]);

  const filteredData = useMemo(
    () => data.filter((win) => matchesFilters(win, filters)),
    [data, filters],
  );

  const setFilters = (update: Partial<MaintenanceWindowFilters>) =>
    setFilterState((current) => ({ ...current, ...update }));

  const handleAction = (action: string, item: MaintenanceWindow) => {
    switch (action) {
      case 'refresh':
        refresh();
        break;
      case 'edit':
        setEditItem(item);
        break;
      case 'approve':
        sendApproval(item.id, 'approve');
        break;
      case 'reject':
        sendApproval(item.id, 'reject');
        break;
      default:
        break;
    }
  };

  return (
    <MaintenanceWindowsV2Context.Provider
      value={{
        loading,
        failed,
        data,
        filteredData,
        filters,
        setFilters,
        now,
        sending,
        onAction: handleAction,
      }}
    >
      {children}
      {editItem !== null && (
        <MaintenanceWindowEditDialog
          open
          onClose={() => {
            setEditItem(null);

            refresh();
          }}
          maintenanceWindow={editItem}
        />
      )}
    </MaintenanceWindowsV2Context.Provider>
  );
};

export default MaintenanceWindowsV2Container;
